import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import {
  CsvLoadErrorItem,
  CsvLoadService,
  CsvLoadType,
  CsvLoadSuccess,
} from '@app/core/csv-load/csv-load.service';

@Component({
  selector: 'app-csv-chip-loader',
  templateUrl: './csv-chip-loader.component.html',
  styleUrls: ['./csv-chip-loader.component.css'],
})
export class CsvChipLoaderComponent {
  @Input() type: CsvLoadType = 'NHC';
  @Input() queryName = '';
  @Input() hasExistingData = false;
  @Input() loadedCount = 0;
  @Output() valuesLoaded = new EventEmitter<CsvLoadSuccess>();
  @Output() loadError = new EventEmitter<CsvLoadErrorItem[]>();
  @Output() cleared = new EventEmitter<void>();

  public loading = false;
  public reportDialogVisible = false;
  public reportContent = '';
  public reportFilename = '';

  constructor(
    private readonly csvLoadService: CsvLoadService,
    private readonly toastr: ToastrService,
    private readonly confirmationService: ConfirmationService
  ) {}

  get stateLabel(): string {
    return this.loadedCount ? `${this.loadedCount} registros cargados` : '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.toastr.error('Formato no soportado. Solo se permite .csv');
      input.value = '';
      return;
    }

    if (this.hasExistingData) {
      this.confirmationService.confirm({
        message:
          'El campo ya contiene datos. Esta carga CSV sobrescribira su contenido. Desea continuar?',
        accept: () => this.upload(file, input),
        reject: () => {
          input.value = '';
        },
      });
      return;
    }

    this.upload(file, input);
  }

  onClear(): void {
    this.reportContent = '';
    this.reportFilename = '';
    this.reportDialogVisible = false;
    this.cleared.emit();
  }

  private upload(file: File, input: HTMLInputElement): void {
    this.loading = true;
    this.csvLoadService.uploadCsv(this.type, file, this.queryName).subscribe({
      next: (result) => {
        this.valuesLoaded.emit(result);
        this.setSuccessReport(result);
        this.toastr.success(`${result.total_cargados} registros cargados`);
        input.value = '';
        this.loading = false;
      },
      error: (error) => {
        const errors = error?.error?.errors || [];
        this.loadError.emit(errors);
        this.setErrorReport(errors);
        this.toastr.error('No se pudo cargar el CSV');
        input.value = '';
        this.loading = false;
      },
    });
  }

  public openReport(): void {
    this.reportDialogVisible = true;
  }

  public downloadReport(): void {
    if (!this.reportContent || !this.reportFilename) {
      return;
    }
    const blob = new Blob([this.reportContent], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.reportFilename;
    link.click();
    URL.revokeObjectURL(url);
  }

  private setSuccessReport(result: CsvLoadSuccess): void {
    this.reportContent =
      `Resultado: CARGA EXITOSA\n` +
      `Tipo: ${this.type}\n` +
      `Total filas fichero: ${result.total_filas}\n` +
      `Total duplicados eliminados: ${result.total_duplicados}\n` +
      `Total registros cargados: ${result.total_cargados}\n`;
    this.reportFilename = this.buildFilename();
  }

  private setErrorReport(errors: CsvLoadErrorItem[]): void {
    const details = errors
      .map(
        (item) =>
          `Fila ${item.fila} | Valor "${item.valor || ''}" | ${item.descripcion}`
      )
      .join('\n');
    this.reportContent =
      `Resultado: CARGA FALLIDA\n` +
      `Tipo: ${this.type}\n` +
      `Total errores: ${errors.length}\n` +
      `No se cargo ningun dato.\n\n` +
      details;
    this.reportFilename = this.buildFilename();
  }

  private buildFilename(): string {
    const queryName = (this.queryName || 'ConsultaNueva').replaceAll(
      /[^a-zA-Z0-9_-]/g,
      '_'
    );
    const datePart = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    const typePart = this.type === 'PETICIONES' ? 'Peticiones' : 'NHC';
    return `${queryName}_${typePart}_${datePart}.txt`;
  }
}
